import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, ChevronRight, Clock, Trash2, Pencil, CalendarClock, Moon } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { SearchBox } from "@/components/SearchBox";
import { TimePicker } from "@/components/TimePicker";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SERVICES, ENTITY_KINDS, entityKindOf } from "@/lib/services";
import { AbsenceBands, hasFullDayAbsence } from "@/components/AgendaAbsence";
import { Switch } from "@/components/ui/switch";
import { Bell, BellOff } from "lucide-react";
import { enablePush, disablePush, pushStatus, needsIOSInstall, canUsePush, sendTestPush } from "@/lib/push";

// Interruttore notifiche push delle timbrature suggerite (in cima all'Agenda).
function NotifToggle() {
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [iosHint, setIosHint] = useState(false);
  const [unsupported, setUnsupported] = useState(false);

  useEffect(() => {
    if (needsIOSInstall()) { setIosHint(true); return; }
    if (!canUsePush()) { setUnsupported(true); return; }
    pushStatus().then(setOn).catch(() => {});
  }, []);

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (!on) {
        await enablePush();
        setOn(true);
        toast.success("Notifiche attivate", { description: "Riceverai i suggerimenti di timbratura anche fuori dall'app." });
      } else {
        await disablePush();
        setOn(false);
        toast.message("Notifiche disattivate");
      }
    } catch (err) {
      if (err.code === "ios-install") { setIosHint(true); }
      else if (err.code === "denied") { toast.error("Permesso negato", { description: "Abilita le notifiche dalle impostazioni del browser." }); }
      else if (err.code === "unsupported") { setUnsupported(true); toast.error("Notifiche non supportate su questo browser"); }
      else { toast.error("Attivazione non riuscita"); }
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-4 mb-5" data-testid="notif-toggle-card">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-[#EDF5FA] border border-[#C4D8E6] flex items-center justify-center shrink-0">
          {on ? <Bell className="h-5 w-5 text-[#22364D]" /> : <BellOff className="h-5 w-5 text-slate-400" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-heading font-semibold text-slate-800">Notifiche timbrature suggerite</p>
          <p className="text-xs font-medium mt-0.5" style={{ color: on ? "#2E7D5B" : "#94A3B8" }} data-testid="notif-toggle-state">
            {on ? "Attive" : "Disattive"}
          </p>
        </div>
        {!iosHint && !unsupported && (
          <Switch checked={on} disabled={busy} onCheckedChange={toggle} data-testid="notif-toggle-switch" />
        )}
      </div>
      {on && (
        <button data-testid="notif-test-btn" disabled={testing}
          onClick={async () => {
            if (testing) return;
            setTesting(true);
            try {
              await sendTestPush();
              toast.success("Notifica di prova inviata", { description: "Dovresti riceverla tra pochi secondi." });
            } catch (err) {
              const detail = err?.response?.data?.detail || (err?.code === "no-endpoint" ? "Nessuna subscription su questo dispositivo." : "Invio non riuscito.");
              toast.error("Prova non riuscita", { description: detail });
            } finally { setTesting(false); }
          }}
          className="mt-3 w-full text-sm font-semibold rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-60 py-2 transition-colors">
          {testing ? "Invio in corso…" : "Invia notifica di prova"}
        </button>
      )}
      {iosHint && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3" data-testid="notif-ios-hint">
          Per attivare le notifiche su iPhone/iPad, aggiungi Orbit alla schermata Home (Condividi → «Aggiungi a Home») e riaprilo dall'icona.
        </p>
      )}
      {unsupported && (
        <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 mt-3" data-testid="notif-unsupported">
          Le notifiche non sono supportate su questo browser.
        </p>
      )}
    </div>
  );
}

const DAYS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];

// Dialog per aggiungere / modificare una voce dell'Agenda.
// Riusa il catalogo servizi (my-services), i selettori per entity_kind e le stesse fonti dati
// della timbratura. In più raccoglie orario di inizio e fine.
function SlotDialog({ open, onClose, onSaved, day, enabledKeys, editSlot }) {
  const isEdit = !!editSlot;
  const [step, setStep] = useState("service");     // service | role | entity | time
  const [slug, setSlug] = useState("");
  const [role, setRole] = useState("");
  const [entity, setEntity] = useState(null);      // {id, label, sublabel}
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [saving, setSaving] = useState(false);

  const cfg = slug ? SERVICES[slug] : null;
  const roleGated = !!cfg?.roleEntity;
  const effKindName = roleGated ? (role ? cfg.roleEntity[role] : null) : cfg?.entityKind;
  const kind = !cfg ? null : (roleGated ? (effKindName ? ENTITY_KINDS[effKindName] : null) : entityKindOf(cfg));

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      // In modifica: servizio/entità già noti, si parte dagli orari (riusa la stessa entità).
      setSlug(editSlot._slug); setRole(editSlot.role_or_task || "");
      setEntity({ id: editSlot.entity_id, label: editSlot.titolo, sublabel: editSlot.sottotitolo });
      setStartTime(editSlot.start_time); setEndTime(editSlot.end_time);
      setStep("time");
    } else {
      setStep("service"); setSlug(""); setRole(""); setEntity(null);
      setItems([]); setSearch(""); setStartTime(""); setEndTime("");
    }
  }, [open, isEdit, editSlot]);

  const loadEntities = useCallback(async (q) => {
    if (!cfg || !kind) return;
    setLoading(true);
    try {
      const base = kind.listApi(cfg.key);
      const sep = base.includes("?") ? "&" : "?";
      const url = kind.searchable ? `${base}${sep}search=${encodeURIComponent(q || "")}` : base;
      const { data } = await api.get(url);
      setItems(data || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [cfg, kind]);

  useEffect(() => {
    if (step === "entity" && kind) loadEntities("");
  }, [step, kind, loadEntities]);

  useEffect(() => {
    if (step !== "entity" || !kind?.searchable) return;
    const t = setTimeout(() => loadEntities(search), 250);
    return () => clearTimeout(t);
  }, [search, step, kind, loadEntities]);

  const chooseService = (s) => {
    setSlug(s);
    const c = SERVICES[s];
    if (c.roles && c.roles.length) setStep("role");
    else setStep("entity");
  };
  const chooseRole = (r) => { setRole(r); setStep("entity"); };
  const chooseEntity = (d) => {
    setEntity({ id: d.id, label: kind.title(d), sublabel: kind.subtitle(d) });
    setStep("time");
  };

  const save = async () => {
    if (!startTime || !endTime) { toast.error("Indica orario di inizio e fine"); return; }
    setSaving(true);
    try {
      const payload = {
        day_of_week: day, service_type: cfg.key, entity_id: entity.id,
        entity_kind: kind?.backendKind || effKindName || cfg.entityKind,
        role_or_task: cfg.roles ? role : null,
        start_time: startTime, end_time: endTime,
      };
      if (isEdit) await api.put(`/agenda/slots/${editSlot.id}`, payload);
      else await api.post("/agenda/slots", payload);
      toast.success(isEdit ? "Voce aggiornata" : "Servizio aggiunto all'agenda");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Salvataggio non riuscito"); }
    finally { setSaving(false); }
  };

  const enabledServiceEntries = Object.entries(SERVICES).filter(([, c]) => enabledKeys?.has(c.key));
  const filteredEntities = !kind ? [] : kind.searchable ? items
    : items.filter((d) => `${kind.title(d)} ${kind.subtitle(d)}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-w-lg max-h-[92vh] overflow-y-auto" data-testid="agenda-slot-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {isEdit ? "Modifica servizio programmato" : "Aggiungi servizio"} · {DAYS[day]}
          </DialogTitle>
          <DialogDescription>
            {step === "service" && "Seleziona il servizio"}
            {step === "role" && "Seleziona il ruolo della prestazione"}
            {step === "entity" && (cfg?.terms?.hint ?? kind?.hint)}
            {step === "time" && "Indica l'orario di inizio e di fine"}
          </DialogDescription>
        </DialogHeader>

        {step === "service" && (
          <div className="space-y-2" data-testid="agenda-step-service">
            {enabledServiceEntries.length === 0 ? (
              <p className="text-sm text-slate-500">Nessun servizio abilitato per il tuo profilo.</p>
            ) : enabledServiceEntries.map(([s, c]) => {
              const Icon = c.icon;
              return (
                <button key={s} onClick={() => chooseService(s)} data-testid={`agenda-service-${s}`}
                  className="w-full text-left rounded-xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-all active:scale-[0.99] p-3.5 flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-lg ${c.accent} flex items-center justify-center`}><Icon className="h-5 w-5 text-primary" /></div>
                  <span className="font-semibold text-slate-800 flex-1">{c.label}</span>
                  <ChevronRight className="h-5 w-5 text-slate-400" />
                </button>
              );
            })}
          </div>
        )}

        {step === "role" && (
          <div className="space-y-2" data-testid="agenda-step-role">
            {cfg.roles.map((r) => (
              <button key={r} onClick={() => chooseRole(r)} data-testid={`agenda-role-${r}`}
                className="w-full text-left rounded-xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-all active:scale-[0.99] p-3.5 flex items-center gap-3">
                <span className="font-semibold text-slate-800 flex-1">{r}</span>
                <ChevronRight className="h-5 w-5 text-slate-400" />
              </button>
            ))}
          </div>
        )}

        {step === "entity" && (
          <div className="space-y-2.5" data-testid="agenda-step-entity">
            {kind?.searchable && <SearchBox value={search} onChange={setSearch} testid="agenda-entity-search" placeholder={cfg?.terms?.searchPlaceholder ?? kind?.searchPlaceholder} />}
            {loading ? <p className="text-sm text-slate-500">Caricamento…</p>
              : filteredEntities.length === 0 ? (
                <Card className="rounded-xl border-slate-200 shadow-sm"><CardContent className="p-6 text-center text-slate-500 text-sm" data-testid="agenda-entity-empty">{cfg?.terms?.emptyMessage ?? kind?.emptyMessage}</CardContent></Card>
              ) : filteredEntities.map((d) => (
                <button key={d.id} onClick={() => chooseEntity(d)} data-testid={`agenda-entity-${d.id}`}
                  className="w-full text-left rounded-xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-all active:scale-[0.99] p-3.5 flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-slate-800 truncate">{kind.title(d)}</p>
                      {kind.showAppaltoBadge && d.appalto_nome && <Badge className="bg-primary/10 text-primary hover:bg-primary/10 border-0 text-[10px] font-semibold">{d.appalto_nome}</Badge>}
                    </div>
                    <p className="text-sm text-slate-500 truncate">{kind.subtitle(d)}</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-slate-400 shrink-0" />
                </button>
              ))}
          </div>
        )}

        {step === "time" && (
          <div className="space-y-4" data-testid="agenda-step-time">
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
              <p className="font-semibold text-slate-800">{cfg?.label}{role ? ` · ${role}` : ""}</p>
              <p className="text-sm text-slate-500">{entity?.label}{entity?.sublabel ? ` · ${entity.sublabel}` : ""}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <TimePicker label="Inizio" value={startTime} onChange={setStartTime} testid="agenda-start" />
              <TimePicker label="Fine" value={endTime} onChange={setEndTime} testid="agenda-end" />
            </div>
            {startTime && endTime && endTime <= startTime && (
              <p className="text-xs text-amber-600 flex items-center gap-1.5"><Moon className="h-3.5 w-3.5" /> Il servizio prosegue oltre la mezzanotte e sarà mostrato anche sul giorno successivo.</p>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {step === "time" && !isEdit && (
            <Button variant="outline" onClick={() => setStep(cfg?.roles ? "entity" : "entity")} data-testid="agenda-back-step" className="rounded-lg mr-auto">Indietro</Button>
          )}
          <Button variant="ghost" onClick={onClose} data-testid="agenda-cancel">Annulla</Button>
          {step === "time" && (
            <Button onClick={save} disabled={saving} data-testid="agenda-save" className="rounded-lg">{saving ? "Salvataggio…" : "Salva"}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AgendaView() {
  const navigate = useNavigate();
  const [slots, setSlots] = useState([]);
  const [absences, setAbsences] = useState({});
  const [enabledKeys, setEnabledKeys] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [addDay, setAddDay] = useState(null);
  const [detail, setDetail] = useState(null);
  const [editSlot, setEditSlot] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [delBusy, setDelBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/agenda/slots");
      setSlots(data || []);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Errore caricamento agenda"); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    api.get("/my-services").then((r) => setEnabledKeys(new Set((r.data || []).map((s) => s.slug)))).catch(() => {});
    api.get("/agenda/absences").then((r) => {
      const m = {};
      (r.data?.days || []).forEach((d) => { m[d.dow] = d.absences || []; });
      setAbsences(m);
    }).catch(() => {});
  }, []);

  const slugOf = (key) => Object.keys(SERVICES).find((s) => SERVICES[s].key === key) || "";
  const iconOf = (key) => (SERVICES[slugOf(key)]?.icon) || CalendarClock;

  // Espande le voci per giorno di visualizzazione (mezzanotte: appare su due giorni).
  const byDay = (dow) => {
    const out = [];
    slots.forEach((s) => {
      if (s.day_of_week === dow) out.push({ ...s, _disp: "start", _from: s.start_time, _to: s.overnight ? "24:00" : s.end_time });
      if (s.overnight && ((s.day_of_week + 1) % 7) === dow) out.push({ ...s, _disp: "end", _from: "00:00", _to: s.end_time });
    });
    return out.sort((a, b) => a._from.localeCompare(b._from));
  };

  const openDetail = (s) => setDetail(s);
  const startEdit = () => { const d = detail; setDetail(null); setEditSlot({ ...d, _slug: slugOf(d.service_type) }); };
  const doDelete = async () => {
    if (!toDelete) return;
    setDelBusy(true);
    try {
      await api.delete(`/agenda/slots/${toDelete.id}`);
      toast.success("Voce eliminata");
      setToDelete(null); setDetail(null); await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Eliminazione non riuscita"); }
    finally { setDelBusy(false); }
  };

  return (
    <div className="worker-theme min-h-screen bg-[#EDF0F4] pb-10">
      <AppHeader />
      <main className="max-w-3xl mx-auto px-4 py-6">
        <button onClick={() => navigate("/lavoratore")} data-testid="agenda-back-home"
          className="inline-flex items-center text-sm text-slate-500 hover:text-primary mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" /> Home
        </button>
        <div className="flex items-center gap-3 mb-5">
          <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#D6E7F3] to-[#A9C7DD] border border-[#93B4CC] flex items-center justify-center shadow-inner"><CalendarClock className="h-5 w-5 text-[#22364D]" /></div>
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Agenda Settimanale</h1>
            <p className="text-sm text-slate-500">La tua settimana tipo ricorrente · si ripete identica ogni settimana</p>
          </div>
        </div>

        {loading ? (
          <div className="space-y-3">{[0, 1, 2, 3].map((i) => <div key={i} className="rounded-2xl bg-white border border-slate-200 h-24 animate-pulse" />)}</div>
        ) : (
          <div className="space-y-4" data-testid="agenda-days">
            <NotifToggle />
            {DAYS.map((dname, dow) => {
              const items = byDay(dow);
              const dayAbsences = absences[dow] || [];
              const dimmed = hasFullDayAbsence(dayAbsences);
              return (
                <section key={dow} className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid={`agenda-day-${dow}`}>
                  <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#D9E9F5] to-[#C7DCEE] border-b-2 border-[#7FB2D4]">
                    <h2 className="font-heading font-bold text-[#22364D] flex items-center gap-2"><span className="inline-block h-4 w-1.5 rounded-full bg-[#C6A3B4]" />{dname}</h2>
                    <Button size="sm" variant="ghost" onClick={() => setAddDay(dow)} data-testid={`agenda-add-${dow}`}
                      className="text-[#22364D] hover:bg-[#EDF5FA] h-8">
                      <Plus className="h-4 w-4 mr-1.5" /> Aggiungi servizio
                    </Button>
                  </div>
                  <div className="p-3">
                    <AbsenceBands absences={dayAbsences} />
                    <div className="space-y-2">
                    {items.length === 0 ? (
                      <p className="text-sm text-slate-400 px-1 py-1.5" data-testid={`agenda-empty-${dow}`}>Nessun servizio programmato</p>
                    ) : items.map((s) => {
                      const Icon = iconOf(s.service_type);
                      const isSub = s.is_substitution;
                      return (
                        <button key={`${s.id}-${s._disp}`} onClick={() => openDetail(s)} data-testid={`agenda-slot-${s.id}-${s._disp}`}
                          className={`w-full text-left rounded-xl border transition-all active:scale-[0.99] p-3 flex items-center gap-3 hover:shadow-md ${dimmed ? "opacity-50" : ""} ${isSub ? "border-[#D9CEEC] bg-[#F1ECF9] hover:border-[#B9A7DC]" : "border-slate-200 hover:border-primary/30"}`}>
                          <div className={`h-9 w-9 rounded-lg border flex items-center justify-center shrink-0 ${isSub ? "bg-white/70 border-[#D9CEEC]" : "bg-[#EDF5FA] border-[#C4D8E6]"}`}><Icon className={`h-4.5 w-4.5 ${isSub ? "text-[#5B4A7D]" : "text-primary"}`} /></div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="inline-flex items-center gap-1 text-sm font-semibold text-slate-800"><Clock className="h-3.5 w-3.5 text-slate-400" />{s._from}–{s._to}</span>
                              {isSub && <Badge className="bg-[#E7DEF5] text-[#5B4A7D] border-0 text-[10px] font-bold">Sostituzione</Badge>}
                              {s._disp === "end" && <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">dal giorno precedente</Badge>}
                              {s._disp === "start" && s.overnight && <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">prosegue domani</Badge>}
                            </div>
                            <p className="text-sm font-medium text-slate-700 truncate">{SERVICES[slugOf(s.service_type)]?.label || s.service_type}{s.role_or_task ? ` · ${s.role_or_task}` : ""}</p>
                            <p className="text-xs text-slate-500 truncate">{isSub && s.date ? `${new Date(s.date + "T00:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "long" })} · ` : ""}{s.titolo}{s.sottotitolo ? ` · ${s.sottotitolo}` : ""}</p>
                          </div>
                          <ChevronRight className="h-5 w-5 text-slate-400 shrink-0" />
                        </button>
                      );
                    })}
                    </div>
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </main>

      {/* Aggiungi */}
      <SlotDialog open={addDay !== null} day={addDay ?? 0} enabledKeys={enabledKeys}
        onClose={() => setAddDay(null)} onSaved={() => { setAddDay(null); load(); }} />

      {/* Modifica */}
      <SlotDialog open={!!editSlot} day={editSlot?.day_of_week ?? 0} enabledKeys={enabledKeys} editSlot={editSlot}
        onClose={() => setEditSlot(null)} onSaved={() => { setEditSlot(null); load(); }} />

      {/* Dettaglio voce */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="rounded-2xl max-w-md" data-testid="agenda-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">{SERVICES[slugOf(detail.service_type)]?.label || detail.service_type}</DialogTitle>
                <DialogDescription>{DAYS[detail.day_of_week]} · {detail.start_time}–{detail.end_time}{detail.overnight ? " (oltre la mezzanotte)" : ""}</DialogDescription>
              </DialogHeader>
              <div className="space-y-2 text-sm">
                {detail.is_substitution && detail.date && <p><span className="text-slate-500">Data:</span> <b className="capitalize">{new Date(detail.date + "T00:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" })}</b></p>}
                {detail.role_or_task && <p><span className="text-slate-500">Ruolo:</span> <b>{detail.role_or_task}</b></p>}
                <p><span className="text-slate-500">Servizio:</span> <b>{detail.titolo}</b></p>
                {detail.sottotitolo && <p className="text-slate-500">{detail.sottotitolo}</p>}
              </div>
              {detail.is_substitution ? (
                <div className="rounded-lg border border-[#D9CEEC] bg-[#F1ECF9] px-3 py-2 text-xs text-[#5B4A7D]" data-testid="agenda-detail-sub-note">
                  Impegno di <b>sostituzione</b> temporaneo: ha precedenza operativa nella sua fascia. Si gestisce dalla Bacheca Sostituzioni.
                </div>
              ) : (
                <DialogFooter className="gap-2">
                  <Button variant="ghost" onClick={() => setToDelete(detail)} data-testid="agenda-detail-delete" className="text-destructive hover:bg-destructive/5 mr-auto">
                    <Trash2 className="h-4 w-4 mr-1.5" /> Elimina
                  </Button>
                  <Button onClick={startEdit} data-testid="agenda-detail-edit" className="rounded-lg"><Pencil className="h-4 w-4 mr-1.5" /> Modifica</Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="agenda-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questo servizio programmato?</AlertDialogTitle>
            <AlertDialogDescription>La voce verrà rimossa dalla tua Agenda Settimanale. Non influisce sulle timbrature né sul Libro Presenze.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="agenda-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} disabled={delBusy} data-testid="agenda-delete-confirm" className="bg-destructive hover:bg-destructive/90">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
